/**
 * planPreviewUtils · 文件夹方案预览的节点构造与统计
 *
 * 反选标识统一为后端文件夹 key:
 *   · 实时 preview 用 PreviewFolder.key
 *   · 历史树用 PlanFolder.chainKey (接口 5 excluded_folder_keys / confirm excluded_keys 同源)
 *
 * 祖先级联排除不再依赖路径前缀，由 PlanFolderRow 沿树结构传递 ancestorExcluded。
 */

import type { PlanFolder, FollowUpChip } from '../../../../types/ai-classify'
import type { PlanPreviewNode } from './types'

/**
 * PlanFolder 森林 → View 层节点 (历史模式一次性构造完整树)
 *
 * key 用 folder id 作 React/展开态标识; excludeKey 用 chainKey 作反选标识。
 * chainKey 缺失的节点 excludeKey 退化为空串 —— 该节点无法被反选 (兜底, 正常不应出现)。
 */
export function planFoldersToPreviewNodes(
  folders: PlanFolder[]
): PlanPreviewNode[] {
  return folders.map((f) => {
    const childNodes =
      Array.isArray(f.children) && f.children.length > 0
        ? planFoldersToPreviewNodes(f.children)
        : []
    return {
      key: f.id,
      name: f.folderName,
      excludeKey: f.chainKey ?? '',
      hasChildren: childNodes.length > 0,
      childCount: childNodes.length > 0 ? childNodes.length : undefined,
      children: childNodes
    }
  })
}

export function collectAllNodeIds(folders: PlanFolder[]): Set<string> {
  const out = new Set<string>()
  function walk(list: PlanFolder[]): void {
    for (const f of list) {
      out.add(f.id)
      if (f.children && f.children.length > 0) walk(f.children)
    }
  }
  walk(folders)
  return out
}

export function countAllNodes(folders: PlanFolder[]): number {
  let n = 0
  function walk(list: PlanFolder[]): void {
    for (const f of list) {
      n += 1
      if (f.children && f.children.length > 0) walk(f.children)
    }
  }
  walk(folders)
  return n
}

/**
 * 统计"有效选中"的节点数 · 节点自身或任一祖先被排除即不计入（级联）。
 * 级联语义与 PlanFolderRow 沿树传递的 ancestorExcluded 一致。
 * chainKey 为空的节点无法被反选，永远计入（除非祖先被排除）。
 *
 * 单趟 O(n) 遍历，n 受后端 max_expand_size(~2000) 上限约束，开销可忽略。
 */
export function countIncludedNodes(
  folders: PlanFolder[],
  excludedSet: Set<string>
): number {
  let n = 0
  function walk(list: PlanFolder[], ancestorExcluded: boolean): void {
    for (const f of list) {
      const excluded =
        ancestorExcluded || (!!f.chainKey && excludedSet.has(f.chainKey))
      if (!excluded) n += 1
      if (f.children && f.children.length > 0) walk(f.children, excluded)
    }
  }
  walk(folders, false)
  return n
}

/** 「你可以继续问」chips 展示上限 · 接口契约至多 4 个，UI 也按 4 个设计 */
export const FOLLOW_UP_CHIPS_MAX = 4

/**
 * /session/preview 的 follow_ups → 可渲染的追问 chips（系统边界校验）
 *
 * 下钻 / 翻页响应为 null、字段缺失、混入脏项（key/text 非字符串、text 空）
 * 一律过滤，超过 4 个截断 —— 渲染层拿到即可直接 map，无需再判空。
 *
 * @param rawFollowUps - preview 响应里的 follow_ups（未校验的原始数据）
 * @returns 至多 4 个合法 chip；无可用项时为空数组（渲染层整块不出）
 *
 * @example
 * ```ts
 * sanitizeFollowUpChips([{ key: 'time', text: '只要最近一个月的' }, { key: 'bad' }])
 * // → [{ key: 'time', text: '只要最近一个月的' }]
 * sanitizeFollowUpChips(null) // → []
 * ```
 */
export function sanitizeFollowUpChips(rawFollowUps: unknown): FollowUpChip[] {
  if (!Array.isArray(rawFollowUps)) return []
  return rawFollowUps
    .filter(
      (chip): chip is FollowUpChip =>
        typeof chip === 'object' &&
        chip !== null &&
        typeof (chip as FollowUpChip).key === 'string' &&
        typeof (chip as FollowUpChip).text === 'string' &&
        (chip as FollowUpChip).text !== ''
    )
    .slice(0, FOLLOW_UP_CHIPS_MAX)
}
